/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useState, useEffect} from 'react'
import {useAuth} from '../../modules/auth'
import {debounce} from 'lodash'
import {Helmet} from 'react-helmet'
import {collection, doc, onSnapshot} from 'firebase/firestore'
import {format, parse} from 'date-fns'
import {ListLoading} from 'app/components/ListLoading'
import db from 'services/firebase'
import FilterBar from './components/Filters/FilterBar'
import {toAbsoluteUrl} from '_metronic/helpers'
import StatusBadge from 'app/components/StatusBadge'
import ViewUser from './components/Drawer/ViewUser'
import OwnerDisplay from 'app/components/OwnerDisplay'
import {useLocation} from 'react-router-dom'
import * as XLSX from 'xlsx'

type Props = {
  className: string
}

const AccessReview: React.FC<Props> = () => {
  const [assets, setAssets] = useState<any[]>([])
  const {currentUser} = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredItems, setFilteredItems] = useState<any[]>([])
  const [searchLength, setSearchLength] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [sortBy, setSortBy] = useState('name')
  const [sortDirection, setSortDirection] = useState('asc')
  const [selectedPlatform, setSelectedPlatform] = useState('')
  const [selectedOwner, setSelectedOwner] = useState('')
  const [owners, setOwners] = useState<any[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [userID, setUserID] = useState('')
  const platforms = Array.from(new Set(assets.map((item) => item.platform))).filter(Boolean)
  const location = useLocation()
  const [selectedIntegration, setSelectedIntegration] = useState<any>(null)

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const system = queryParams.get('system')
    const userId = queryParams.get('userId')

    if (system) {
      setSelectedPlatform(system)
    }

    if (userId) {
      setUserID(userId)
      setIsModalOpen(true)
    }
  }, [location.search])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 2000)
    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    getAccess()
  }, [])

  useEffect(() => {
    getIntegrations()
  }, [selectedPlatform])

  const getIntegrations = () => {
    const collectionRef = db.collection('integrations')

    const unsubscribe = collectionRef.onSnapshot((snapshot) => {
      const integrations: any[] = []
      snapshot.forEach((doc) => {
        const data = doc.data()
        integrations.push(data)
      })
      const selectedIntegration = integrations.find((item) => item.name === selectedPlatform)
      setSelectedIntegration(selectedIntegration)
    })
  }

  useEffect(() => {
    applyFilters()
  }, [assets, searchTerm, selectedPlatform, selectedOwner])

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'employees')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      const risksData = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          end_date: doc.data().end_date,
          role: doc.data().role,
        }))
        .filter((data) => !data.end_date)
      setOwners(risksData.filter((item) => item.role !== 'Auditor'))
    })

    return () => unsubscribe()
  }, [currentUser])

  const handleSort = (column: string) => {
    const newSortDirection = column === sortBy && sortDirection === 'asc' ? 'desc' : 'asc'
    setSortBy(column)
    setSortDirection(newSortDirection)

    const sortedItems = [...filteredItems].sort((a, b) => {
      if (
        column === 'name' ||
        column === 'os_version' ||
        column === 'accountRole' ||
        column === 'status'
      ) {
        const valueA = a[column] || ''
        const valueB = b[column] || ''
        return newSortDirection === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA)
      } else if (column === 'owner') {
        const valueA = a.owner?.name || ''
        const valueB = b.owner?.name || ''
        return newSortDirection === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA)
      } else if (column === 'last_checkedin') {
        const dateA = new Date(a[column]).getTime()
        const dateB = new Date(b[column]).getTime()
        return newSortDirection === 'asc' ? dateA - dateB : dateB - dateA
      }
      return 0
    })

    setFilteredItems(sortedItems)
  }

  const getAccess = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'access_review')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const allMembers: any[] = []
        snapshot.docs.forEach((doc) => {
          const assetData = doc.data()
          const members = assetData.members || {}
          const org = assetData.org || {}

          Object.keys(members).forEach((memberKey) => {
            const member = members[memberKey]
            allMembers.push({
              id: `${doc.id}-${memberKey}`,
              login: member.login,
              name: member.name,
              isTwoFactorEnabled: member.isTwoFactorEnabled,
              platform: assetData.platform,
              orgLogin: org.login,
              orgName: org.name,
              status: member.status,
              createdAt: member.createdAt,
              updatedAt: member.updatedAt,
              tenantId: assetData.tenantId,
              uid: assetData.uid,
              owner: member.owner,
              accountRole: member.accountRole,
            })
          })
        })
        setAssets(allMembers)
        setShowEmptyState(false)
      })

      return () => unsubscribe()
    } catch (error) {
      setShowEmptyState(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...assets]

    if (searchTerm) {
      filtered = filtered.filter((t) => {
        const memberLogin = typeof t.login === 'string' ? t.login.toLowerCase() : ''
        const memberName = typeof t.name === 'string' ? t.name.toLowerCase() : ''
        const createdAt = t.createdAt ? formatTimestamp(t.createdAt).toLowerCase() : ''

        return (
          memberLogin.includes(searchTerm.toLowerCase()) ||
          memberName.includes(searchTerm.toLowerCase()) ||
          createdAt.includes(searchTerm.toLowerCase())
        )
      })
    }

    if (selectedPlatform) {
      filtered = filtered.filter((t) => t.platform === selectedPlatform)
    }

    if (selectedOwner) {
      filtered = filtered.filter((t) => t.owner?.name === selectedOwner)
    }

    setFilteredItems(filtered)
    setSearchLength(filtered.length === 0)
  }

  const debouncedHandleInputChange = debounce((searchValue: string) => {
    setSearchTerm(searchValue)
  }, 100)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase()
    setSearchTerm(searchValue)
    debouncedHandleInputChange(searchValue)
  }

  const handleExport = () => {
    const instructionsData = [
      [
        'Access Review Template Generated by SecureSlate — ' +
          formatTimestamp(new Date().toISOString().split('T')[0]),
      ],
      [''],
      [
        '1. Add new sheets for any systems containing customer data that aren’t already listed, and make sure to include them in the ‘Summary’ tab.',
      ],
      [
        '2. Review each sheet to confirm that the access listed is accurate, complete, and limited to what is necessary. Fill in any missing information as part of your review.',
      ],
      [
        '3. Once each system has been reviewed, update the ‘Summary’ tab to reflect its review status.',
      ],
      ['4. After completing the review, upload the finalized spreadsheet to its respective test.'],
    ]

    const summaryData = [
      {
        Platform: selectedIntegration?.name || '',
        'Platform Category': selectedIntegration?.group || 'Other',
        'Priority Level': 'UNSCORED',
        Department: '',
        Comments: '',
        Owner: '',
        'Data Extracted': '',
        'Review Completion': '',
      },
    ]

    const platformData = filteredItems.map((member) => ({
      'User Name': member.name || '',
      'Account Holder': member.owner?.name || '',
      'Access Role': member.accountRole || '',
      'Permissions Description': '',
      'Last Active': member.updatedAt ? formatTimestamp(member.updatedAt) : '',
      'Account Created': member.createdAt ? formatTimestamp(member.createdAt) : '',
      'Account Deactivated': member.completedAt ? formatTimestamp(member.completedAt) : '',
      'Account Status': member.status
        ? member.status.charAt(0).toUpperCase() + member.status.slice(1)
        : '',
      'MFA Status': member.mfaEnabled ? 'Enabled' : 'Disabled',
      'Access Valid?': '',
      'Modifications (if any)': '',
      'Reviewed By': '',
      'Review Date': '',
      'Approved By': '',
      'Approval Date': '',
    }))

    const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsData)
    instructionsSheet['!cols'] = [{wch: 100}]

    const summarySheet = XLSX.utils.json_to_sheet(summaryData)
    summarySheet['!cols'] = [
      {wch: 20},
      {wch: 20},
      {wch: 15},
      {wch: 20},
      {wch: 30},
      {wch: 20},
      {wch: 20},
      {wch: 20},
    ]

    const platformSheet = XLSX.utils.json_to_sheet(platformData)
    platformSheet['!cols'] = [
      {wch: 20},
      {wch: 20},
      {wch: 15},
      {wch: 30},
      {wch: 20},
      {wch: 20},
      {wch: 20},
      {wch: 15},
      {wch: 15},
      {wch: 15},
      {wch: 30},
      {wch: 20},
      {wch: 15},
      {wch: 20},
      {wch: 15},
    ]

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instructions')
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary')
    XLSX.utils.book_append_sheet(workbook, platformSheet, selectedPlatform || 'Platform')

    XLSX.writeFile(workbook, `access_data_export_${selectedPlatform || 'Platform'}_${new Date().toISOString().split('T')[0]}.xlsx`)
  }

  const formatTimestamp = (dateInput) => {
    if (typeof dateInput === 'string') {
      try {
        const date = parse(dateInput, 'yyyy-MM-dd', new Date())
        return format(date, 'd MMMM, yyyy')
      } catch (error) {
        return 'Invalid Date'
      }
    } else if (dateInput && dateInput.seconds) {
      return format(new Date(dateInput.seconds * 1000), 'd MMMM, yyyy')
    } else if (dateInput instanceof Date) {
      return format(dateInput, 'd MMMM, yyyy')
    } else {
      return 'Invalid Date'
    }
  }

  const handleOpenModal = (id) => {
    setIsModalOpen(true)
    setUserID(id)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setUserID('')
    window.history.replaceState({}, '', '/access')
  }

  return (
    <>
      <Helmet>
        <title>SecureSlate | User Access</title>
      </Helmet>

      <FilterBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        platforms={platforms}
        owners={owners}
        selectedPlatform={selectedPlatform}
        setSelectedPlatform={setSelectedPlatform}
        selectedOwner={selectedOwner}
        setSelectedOwner={setSelectedOwner}
        onExport={handleExport}
        handleInputChange={handleInputChange}
      />

      <div className='card-body py-3'>
        <div className='table-responsive' style={{minHeight: '60vh', overflowY: 'auto'}}>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold fs-7' style={{color: '#5A5A5A'}}>
                <th className='min-w-150px' onClick={() => handleSort('name')}>
                  ACCOUNT{' '}
                  {sortBy === 'name' && (
                    <span
                      className={`ms-1 icon-xxs text-muted ${
                        sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                      }`}
                    />
                  )}
                </th>
                <th className='min-w-100px' onClick={() => handleSort('owner')}>
                  OWNER{' '}
                  {sortBy === 'owner' && (
                    <span
                      className={`ms-1 icon-xxs text-muted ${
                        sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                      }`}
                    />
                  )}
                </th>
                <th className='min-w-100px' onClick={() => handleSort('status')}>
                  STATUS{' '}
                  {sortBy === 'status' && (
                    <span
                      className={`ms-1 icon-xxs text-muted ${
                        sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                      }`}
                    />
                  )}
                </th>
                <th className='min-w-100px' onClick={() => handleSort('accountRole')}>
                  ROLE{' '}
                  {sortBy === 'accountRole' && (
                    <span
                      className={`ms-1 icon-xxs text-muted ${
                        sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                      }`}
                    />
                  )}
                </th>
                <th className='min-w-50px'>2FA</th>
                <th className='min-w-50px'>CREATED ON</th>
                <th className='min-w-50px'>DEACTIVATED ON</th>
              </tr>
            </thead>
            {filteredItems && filteredItems.length > 0 ? (
              filteredItems.map((memberData) => (
                <tbody key={memberData.id}>
                  <tr
                    key={memberData.id}
                    style={{
                      transition: 'background-color 0.2s ease',
                      cursor: 'pointer',
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.backgroundColor = 'oklch(98.5% 0.002 247.839)')
                    }
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    onClick={() => handleOpenModal(memberData.id)}
                  >
                    <td>
                      <span
                        className='fw-semibold d-block fs-6'
                      >
                        {memberData.name}
                      </span>
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>
                        {memberData.owner ? (
                          <OwnerDisplay owner={memberData.owner.name || ''} />
                        ) : (
                          <><svg  xmlns="http://www.w3.org/2000/svg"  width="20"  height="20"  viewBox="0 0 24 24"  fill="none"  stroke="oklch(57.7% 0.245 27.325)"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-alert-triangle"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 9v4" /><path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" /><path d="M12 16h.01" /></svg></>
                        )}
                      </div>
                    </td>
                    <td>
                      {memberData.status ? (
                        <StatusBadge
                          status={
                            memberData.status.charAt(0).toUpperCase() + memberData.status.slice(1)
                          }
                        />
                      ) : (
                        '--'
                      )}
                    </td>
                    <td>
                      {memberData.accountRole ? (
                        <span
                          className='badge px-3 rounded-pill me-1 fw-semibold'
                          style={{border: '1px solid #E1E1E1'}}
                        >
                          {memberData.accountRole.charAt(0).toUpperCase() + memberData.accountRole.slice(1)}
                        </span>
                      ) : (
                        '--'
                      )}
                    </td>
                    <td>
                      <div className='fw-semibold fs-6' style={{color: '#111928'}}>
                        {memberData.isTwoFactorEnabled ? <span>Enabled</span> : <span>Disabled</span>}
                      </div>
                    </td>
                    <td>
                      <span className='tme-2 fs-6 fw-semibold'>
                        {memberData.createdAt ? formatTimestamp(memberData.createdAt) : '--'}
                      </span>
                    </td>
                    <td>
                      <span className='tme-2 fs-6 fw-semibold'>
                        {memberData.completedAt ? formatTimestamp(memberData.completedAt) : '--'}
                      </span>
                    </td>
                  </tr>
                </tbody>
              ))
            ) : (
              <tbody>
                <tr style={{height: '100%'}}>
                  {searchLength ? (
                    <td colSpan={10}>
                      <div
                        className='d-flex justify-content-center align-items-center text-center'
                        style={{height: '100%'}}
                      >
                        <div className='m-20'>
                          <img src={toAbsoluteUrl('/media/buttons/emptystate.svg')} alt='' />
                          <div className='m-5 fs-5'>
                            <p style={{fontWeight: 'bold'}}>No Members Found</p>
                          </div>
                          <p>
                            We couldn't find any members that match your search. Maybe try a
                            different search!
                          </p>
                        </div>
                      </div>
                    </td>
                  ) : (
                    <td colSpan={10}>
                      <div
                        className='d-flex justify-content-center align-items-center text-center'
                        style={{height: '100%'}}
                      >
                        {!showEmptyState ? (
                          <ListLoading />
                        ) : (
                          <div className='m-20'>
                            <img src={toAbsoluteUrl('/media/buttons/emptystate.svg')} alt='' />
                            <div className='m-5 fs-5'>
                              <p style={{fontWeight: '600'}}>No Members Added</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              </tbody>
            )}
          </table>
        </div>
      </div>
      <ViewUser isOpen={isModalOpen} onClose={handleCloseModal} id={userID} />
    </>
  )
}

export {AccessReview}
